import * as React from 'react'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { DateRange } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'

export interface DateRangePickerProps {
	dateRange?: DateRange
	onDateRangeChange?: (range: DateRange | undefined) => void
	placeholder?: string
	disabled?: boolean
	minDate?: Date
	maxDate?: Date
	className?: string
	numberOfMonths?: number
	/** Whether to show a save button or auto-close on selection */
	showSaveButton?: boolean
	saveButtonLabel?: string
	onSave?: (range: DateRange | undefined) => void
}

export function DateRangePicker({
	dateRange,
	onDateRangeChange,
	placeholder = 'Pick a date range',
	disabled = false,
	minDate,
	maxDate,
	className,
	numberOfMonths = 2,
	showSaveButton = false,
	saveButtonLabel = 'Save',
	onSave,
}: DateRangePickerProps) {
	const [open, setOpen] = React.useState(false)
	const [internalRange, setInternalRange] = React.useState<
		DateRange | undefined
	>(dateRange)

	// Sync internal state with prop
	React.useEffect(() => {
		setInternalRange(dateRange)
	}, [dateRange])

	const handleSelect = (range: DateRange | undefined) => {
		setInternalRange(range)
		if (!showSaveButton) {
			onDateRangeChange?.(range)
			// Auto-close when both dates are selected
			if (range?.from && range?.to) {
				setOpen(false)
			}
		}
	}

	const handleSave = () => {
		onDateRangeChange?.(internalRange)
		onSave?.(internalRange)
		setOpen(false)
	}

	const formatDateRange = () => {
		if (!dateRange?.from) return placeholder
		if (!dateRange.to) return format(dateRange.from, 'LLL dd, y')
		return `${format(dateRange.from, 'LLL dd, y')} - ${format(dateRange.to, 'LLL dd, y')}`
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					disabled={disabled}
					className={cn(
						'w-full justify-start text-left font-normal',
						!dateRange?.from && 'text-muted-foreground',
						className
					)}
				>
					<CalendarIcon className="mr-2 h-4 w-4" />
					{formatDateRange()}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<Calendar
					mode="range"
					defaultMonth={internalRange?.from}
					selected={internalRange}
					onSelect={handleSelect}
					numberOfMonths={numberOfMonths}
					disabled={(date) => {
						if (minDate && date < minDate) return true
						if (maxDate && date > maxDate) return true
						return false
					}}
				/>
				{showSaveButton && (
					<div className="flex items-center justify-end border-t p-3">
						<Button size="sm" onClick={handleSave}>
							{saveButtonLabel}
						</Button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	)
}

